import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button, Modal, Form, Offcanvas } from 'react-bootstrap';
import ReactPaginate from 'react-paginate';
import Select from 'react-select';
import { useAuth } from '../../context/AuthContext';
import withPermission from '../../utils/withPermission';
import Swal from 'sweetalert2';

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${backendUrl}/api/product-pricing`;

const ProductPricing = () => {
    const [pricings, setPricings] = useState([]);
    const [products, setProducts] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        product_id: '',
        license_model: '',
        deployment: '',
        model_type: '',
        per_agent_price: '',
        deployment_cost: '',
        channel_integration_cost: '',
        manday_rate: '',
        minimum_agents: '',
        available_channels: []
    });
    const [isEdit, setIsEdit] = useState(false);
    const [selectedPricing, setSelectedPricing] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageCount, setPageCount] = useState(1);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const debounceTimeoutRef = useRef(null);

    const WithEditPermission = withPermission(({ children }) => children, ['productpricing-edit']);
    const WithDeletePermission = withPermission(({ children }) => children, ['productpricing-delete']);
    const WithCreatePermission = withPermission(({ children }) => children, ['productpricing-create']);

    // Available channels options for react-select
    const channelOptions = [
        { value: 'WhatsApp', label: 'WhatsApp' },
        { value: 'Facebook', label: 'Facebook' },
        { value: 'Instagram', label: 'Instagram' },
        { value: 'Telegram', label: 'Telegram' },
        { value: 'Line', label: 'Line' },
        { value: 'SMS', label: 'SMS' },
        { value: 'Email', label: 'Email' },
        { value: 'Web Chat', label: 'Web Chat' }
    ];

    // License model options
    const licenseModelOptions = [
        'Perpetual',
        'Subscription',
    ];

    // Deployment options
    const deploymentOptions = [
        'Cloud',
        'On-premise',
    ];

    // Model type options
    const modelTypeOptions = [
        'New',
        'Expansion',
    ];

    useEffect(() => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
            fetchProductPricings(1, searchQuery);
        }, searchQuery ? 1500 : 0);

        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, [searchQuery]);

    useEffect(() => {
        fetchProducts();
        fetchProductPricings();
    }, []);

    const fetchProducts = async () => {
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;
            
            const response = await axios.get(`${backendUrl}/api/products`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
            });
            setProducts(response.data.Data || response.data);
            console.log('Fetched products:', response.data.Data);
        } catch (error) {
            console.error('Error fetching products:', error);
        }
    };

    const fetchProductPricings = async (page = 1) => {
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${API_URL}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                params: {
                    page,
                    search: searchQuery,
                },
            });
            setPricings(response.data.data.data || response.data.data);
            setPageCount(response.data.last_page || 1);
            setCurrentPage(response.data.current_page || 1);
        } catch (error) {
            console.error('Error fetching product pricings:', error);
        }
    };

    const handleAddPricing = () => {
        setFormData({
            product_id: '',
            license_model: '',
            deployment: '',
            model_type: '',
            per_agent_price: '',
            deployment_cost: '',
            channel_integration_cost: '',
            manday_rate: '',
            minimum_agents: '',
            available_channels: []
        });
        setIsEdit(false);
        setSelectedPricing(null);
        setShowModal(true);
        setErrors({});
    };

    const handleEditPricing = (pricing) => {
        setFormData({
            product_id: pricing.product_id,
            license_model: pricing.license_model,
            deployment: pricing.deployment,
            model_type: pricing.model_type,
            per_agent_price: pricing.per_agent_price,
            deployment_cost: pricing.deployment_cost,
            channel_integration_cost: pricing.channel_integration_cost,
            manday_rate: pricing.manday_rate,
            minimum_agents: pricing.minimum_agents,
            available_channels: pricing.available_channels || []
        });
        setIsEdit(true);
        setSelectedPricing(pricing);
        setShowModal(true);
        setErrors({});
    };

    const handleDelete = async (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const userInfo = JSON.parse(localStorage.getItem('user-info'));
                    const origin = localStorage.getItem('user-origin');
                    const token = userInfo?.token;
                    
                    await axios.delete(`${API_URL}/${id}`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Tenant-Origin': origin,
                        },
                    });
                    setPricings(pricings.filter(pricing => pricing.id !== id));
                    Swal.fire("Deleted!", "The product pricing has been deleted.", "success");
                } catch (error) {
                    console.error('Error deleting product pricing:', error);
                    Swal.fire("Error!", "Failed to delete product pricing. Please try again.", "error");
                }
            }
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        setErrors(prevErrors => ({
            ...prevErrors,
            [name]: undefined,
        }));
    };

    // Handle channel selection change
    const handleChannelChange = (selectedOptions) => {
        const selectedChannels = selectedOptions ? selectedOptions.map(option => option.value) : [];
        setFormData(prev => ({
            ...prev,
            available_channels: selectedChannels
        }));
    };

    // Get selected values for react-select
    const getSelectedChannels = () => {
        return channelOptions.filter(option => 
            formData.available_channels.includes(option.value)
        );
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const handlePageChange = ({ selected }) => {
        const newPage = selected + 1;
        setCurrentPage(newPage);
        fetchProductPricings(newPage);
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.product_id) newErrors.product_id = 'Product is required';
        if (!formData.license_model) newErrors.license_model = 'License model is required';
      

        return newErrors;
    };

    const handleSubmit = async () => {
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            if (isEdit) {
                const response = await axios.put(`${API_URL}/${selectedPricing.id}`, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    },
                });
                setPricings(pricings.map(pricing => 
                    pricing.id === selectedPricing.id ? response.data.data : pricing
                ));
            } else {
                const response = await axios.post(API_URL, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    },
                });
                setPricings([...pricings, response.data.data]);
            }
            setShowModal(false);
            Swal.fire("Success!", `Product pricing ${isEdit ? 'updated' : 'created'} successfully.`, "success");
        } catch (error) {
            console.error('Error submitting product pricing:', error);
            Swal.fire("Error!", `Failed to ${isEdit ? 'update' : 'create'} product pricing. Please try again.`, "error");
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    // Custom styles for react-select
    const customStyles = {
        control: (base) => ({
            ...base,
            fontSize: '13px',
            minHeight: '38px',
        }),
        menu: (base) => ({
            ...base,
            fontSize: '13px',
            zIndex: 9999,
        }),
        multiValue: (base) => ({
            ...base,
            fontSize: '12px',
        }),
        multiValueLabel: (base) => ({
            ...base,
            fontSize: '12px',
        })
    };

    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row w-100">
                        <div className="col-12">
                            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                <h4 className="mb-sm-0 font-size-18">Product Pricing</h4>
                                <div className="page-title-right">
                                    <ol className="breadcrumb m-0">
                                        <li className="breadcrumb-item active">Product Pricing List</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between mb-3">
                                        <div className="d-flex">
                                            <div className="search-box me-2 mb-2 d-inline-block">
                                                <div className="position-relative">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Search..."
                                                        value={searchQuery}
                                                        onChange={handleSearchChange}
                                                    />
                                                    <i className="bx bx-search-alt search-icon" />
                                                </div>
                                            </div>
                                        </div>
                                        <WithCreatePermission>
                                            <button
                                                type="button"
                                                className="btn rounded-circle border d-flex align-items-center justify-content-center round-create-button"
                                                onClick={handleAddPricing}
                                                data-bs-toggle="tooltip"
                                                data-bs-placement="top"
                                                title="Add Product Pricing"
                                            >
                                                <i className="mdi mdi-plus" style={{ fontSize: "16px" }}></i>
                                            </button>
                                        </WithCreatePermission>
                                    </div>
                                    <table className="table align-middle table-nowrap table-check table-list">
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ width: '20px' }} className="align-middle">
                                                    <div className="form-check font-size-16">
                                                        <input className="form-check-input" type="checkbox" id="checkAll" />
                                                        <label className="form-check-label" htmlFor="checkAll"></label>
                                                    </div>
                                                </th>
                                                <th className="align-middle">#</th>
                                                <th className="align-middle">Product</th>
                                                <th className="align-middle">License Model</th>
                                                <th className="align-middle">Deployment</th>
                                                {/* <th className="align-middle">Per Agent Price</th>
                                                <th className="align-middle">Minimum Agents</th> */}
                                                <th className="align-middle">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Array.isArray(pricings) && pricings.length > 0 ? (
                                                pricings.map((pricing, index) => (
                                                    <tr key={pricing.id}>
                                                        <td>
                                                            <div className="form-check font-size-16">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    id={`pricingCheck${index}`}
                                                                />
                                                                <label
                                                                    className="form-check-label"
                                                                    htmlFor={`pricingCheck${index}`}
                                                                ></label>
                                                            </div>
                                                        </td>
                                                        <td>{index + 1}</td>
                                                        <td>{pricing.product?.product_name || ''}</td>
                                                        <td>{pricing.license_model}</td>
                                                        <td>{pricing.deployment}</td>
                                                        {/* <td>{formatCurrency(pricing.per_agent_price)}</td>
                                                        <td>{pricing.minimum_agents}</td> */}
                                                        <td>
                                                            <div className="dropdown">
                                                                <i
                                                                    className="mdi mdi-dots-horizontal font-size-18"
                                                                    data-bs-toggle="dropdown"
                                                                    aria-expanded="false"
                                                                    style={{ cursor: 'pointer' }}
                                                                ></i>
                                                                <ul className="dropdown-menu dropdown-menu-end font-size-14">
                                                                    <li>
                                                                        <WithEditPermission>
                                                                            <button 
                                                                                className="dropdown-item" 
                                                                                onClick={() => handleEditPricing(pricing)}
                                                                            >
                                                                                Edit
                                                                            </button>
                                                                        </WithEditPermission>
                                                                    </li>
                                                                    <li>
                                                                        <WithDeletePermission>
                                                                            <button 
                                                                                className="dropdown-item" 
                                                                                onClick={() => handleDelete(pricing.id)}
                                                                            >
                                                                                Delete
                                                                            </button>
                                                                        </WithDeletePermission>
                                                                    </li>
                                                                </ul>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="8" className="text-center">
                                                        No product pricing found.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                    <div className="d-flex justify-content-end pagination-sm">
                                        <ReactPaginate
                                            previousLabel={'<'}
                                            nextLabel={'>'}
                                            breakLabel={'...'}
                                            pageCount={pageCount}
                                            marginPagesDisplayed={2}
                                            pageRangeDisplayed={3}
                                            onPageChange={handlePageChange}
                                            containerClassName={'pagination pagination-rounded justify-content-end mb-2'}
                                            pageClassName={'page-item'}
                                            pageLinkClassName={'page-link'}
                                            previousClassName={'page-item previous'}
                                            previousLinkClassName={'page-link'}
                                            nextClassName={'page-item next'}
                                            nextLinkClassName={'page-link'}
                                            breakClassName={'page-item'}
                                            breakLinkClassName={'page-link'}
                                            activeClassName={'active'}
                                        />
                                    </div>

                                    {/* Modal for Add/Edit Product Pricing */}
                                    <Offcanvas show={showModal} onHide={() => setShowModal(false)} placement='end' style={{ width: '480px' }}>
                                        <Offcanvas.Header closeButton>
                                            <Offcanvas.Title>{isEdit ? 'Edit Product Pricing' : 'Add Product Pricing'}</Offcanvas.Title>
                                        </Offcanvas.Header>
                                        <hr style={{ margin: 0 }} /><br/>
                                        <Offcanvas.Body>
                                            <Form>
                                                <Form.Group controlId="product" className="mb-3">
                                                    <Form.Label style={{ fontSize: '13px' }}>Product</Form.Label>
                                                    <Form.Select
                                                        name="product_id"
                                                        value={formData.product_id}
                                                        onChange={handleInputChange}
                                                        className={`from-feild form-control ${errors?.product_id ? 'is-invalid' : ''}`}
                                                        style={{ fontSize: '13px' }}
                                                    >
                                                        <option value="">Select Product</option>
                                                        {Array.isArray(products) && products.length > 0 ? (
    products.map((product) => {
        console.log("📦 Rendering product option:", product.id, product.name);
        return (
            <option key={product.id} value={product.id}>
                {product.product_name }
            </option>
        );
    })
) : (
    <option value="" disabled>No products available</option>
)}
                                                    </Form.Select>
                                                    {errors.product_id && <div className="invalid-feedback" style={{ fontSize: '12px' }}>*{errors.product_id}</div>}
                                                </Form.Group>

                                                <Form.Group controlId="licenseModel" className="mb-3">
                                                    <Form.Label style={{ fontSize: '13px' }}>License Model</Form.Label>
                                                    <Form.Select
                                                        name="license_model"
                                                        value={formData.license_model}
                                                        onChange={handleInputChange}
                                                        className={`form-feild form-control ${errors?.license_model ? 'is-invalid' : ''}`}
                                                        style={{ fontSize: '13px' }}
                                                    >
                                                        <option value="">Select License Model</option>
                                                        {licenseModelOptions.map(option => (
                                                            <option key={option} value={option}>{option}</option>
                                                        ))}
                                                    </Form.Select>
                                                    {errors.license_model && <div className="invalid-feedback" style={{ fontSize: '12px' }}>*{errors.license_model}</div>}
                                                </Form.Group>

                                                <Form.Group controlId="deployment" className="mb-3">
                                                    <Form.Label style={{ fontSize: '13px' }}>Deployment</Form.Label>
                                                    <Form.Select
                                                        name="deployment"
                                                        value={formData.deployment}
                                                        onChange={handleInputChange}
                                                        className={`form-feild form-control ${errors?.deployment ? 'is-invalid' : ''}`}
                                                        style={{ fontSize: '13px' }}
                                                    >
                                                        <option value="">Select Deployment</option>
                                                        {deploymentOptions.map(option => (
                                                            <option key={option} value={option}>{option}</option>
                                                        ))}
                                                    </Form.Select>
                                                    {errors.deployment && <div className="invalid-feedback" style={{ fontSize: '12px' }}>*{errors.deployment}</div>}
                                                </Form.Group>

                                                <Form.Group controlId="modelType" className="mb-3">
                                                    <Form.Label style={{ fontSize: '13px' }}>Model Type</Form.Label>
                                                    <Form.Select
                                                        name="model_type"
                                                        value={formData.model_type}
                                                        onChange={handleInputChange}
                                                        className={`form-feild form-control ${errors?.model_type ? 'is-invalid' : ''}`}
                                                        style={{ fontSize: '13px' }}
                                                    >
                                                        <option value="">Select Model Type</option>
                                                        {modelTypeOptions.map(option => (
                                                            <option key={option} value={option}>{option}</option>
                                                        ))}
                                                    </Form.Select>
                                                    {errors.model_type && <div className="invalid-feedback" style={{ fontSize: '12px' }}>*{errors.model_type}</div>}
                                                </Form.Group>

                                                {/* <Form.Group controlId="perAgentPrice" className="mb-3">
                                                    <Form.Label style={{ fontSize: '13px' }}>Per Agent Price ($)</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.01"
                                                        name="per_agent_price"
                                                        value={formData.per_agent_price}
                                                        onChange={handleInputChange}
                                                        className={`form-control ${errors?.per_agent_price ? 'is-invalid' : ''}`}
                                                        style={{ fontSize: '13px' }}
                                                    />
                                                    {errors.per_agent_price && <div className="invalid-feedback" style={{ fontSize: '12px' }}>*{errors.per_agent_price}</div>}
                                                </Form.Group> */}

                                                {/* <Form.Group controlId="deploymentCost" className="mb-3">
                                                    <Form.Label style={{ fontSize: '13px' }}>Deployment Cost</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.01"
                                                        name="deployment_cost"
                                                        value={formData.deployment_cost}
                                                        onChange={handleInputChange}
                                                        className={`form-control ${errors?.deployment_cost ? 'is-invalid' : ''}`}
                                                        style={{ fontSize: '13px' }}
                                                    />
                                                    {errors.deployment_cost && <div className="invalid-feedback" style={{ fontSize: '12px' }}>*{errors.deployment_cost}</div>}
                                                </Form.Group> */}

                                                <Form.Group controlId="channelIntegrationCost" className="mb-3">
                                                    <Form.Label style={{ fontSize: '13px' }}>Integration Cost</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.01"
                                                        name="channel_integration_cost"
                                                        value={formData.channel_integration_cost}
                                                        onChange={handleInputChange}
                                                        className={`form-feild form-control ${errors?.channel_integration_cost ? 'is-invalid' : ''}`}
                                                        style={{ fontSize: '13px' }}
                                                    />
                                                    {errors.channel_integration_cost && <div className="invalid-feedback" style={{ fontSize: '12px' }}>*{errors.channel_integration_cost}</div>}
                                                </Form.Group>

                                                {/* <Form.Group controlId="mandayRate" className="mb-3">
                                                    <Form.Label style={{ fontSize: '13px' }}>Manday Rate ($)</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.01"
                                                        name="manday_rate"
                                                        value={formData.manday_rate}
                                                        onChange={handleInputChange}
                                                        className={`form-control ${errors?.manday_rate ? 'is-invalid' : ''}`}
                                                        style={{ fontSize: '13px' }}
                                                    />
                                                    {errors.manday_rate && <div className="invalid-feedback" style={{ fontSize: '12px' }}>*{errors.manday_rate}</div>}
                                                </Form.Group> */}

                                                {/* <Form.Group controlId="minimumAgents" className="mb-3">
                                                    <Form.Label style={{ fontSize: '13px' }}>Minimum Agents</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        name="minimum_agents"
                                                        value={formData.minimum_agents}
                                                        onChange={handleInputChange}
                                                        className={`form-control ${errors?.minimum_agents ? 'is-invalid' : ''}`}
                                                        style={{ fontSize: '13px' }}
                                                    />
                                                    {errors.minimum_agents && <div className="invalid-feedback" style={{ fontSize: '12px' }}>*{errors.minimum_agents}</div>}
                                                </Form.Group> */}

                                                <Form.Group controlId="availableChannels" className="mb-3">
                                                    <Form.Label style={{ fontSize: '13px' }}>Channels</Form.Label>
                                                    <Select
                                                        isMulti
                                                        name="available_channels"
                                                        options={channelOptions}
                                                        value={getSelectedChannels()}
                                                        onChange={handleChannelChange}
                                                        className="form-feild basic-multi-select"
                                                        classNamePrefix="select"
                                                        styles={customStyles}
                                                        placeholder="Select channels..."
                                                    />
                                                </Form.Group>
                                            </Form>
                                        </Offcanvas.Body>
                                        <div className="p-3 d-flex justify-content-end border-top">
                                            <Button variant="secondary me-2" onClick={() => setShowModal(false)}>Close</Button>
                                            <Button variant="primary" onClick={handleSubmit} disabled={loading}>
                                                {loading ? 'Saving...' : (isEdit ? 'Update' : 'Save')}
                                            </Button>
                                        </div>
                                    </Offcanvas>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductPricing;